import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Shield, Plus, Trash2, UserX, Loader2, ShieldCheck, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "./ui/button";

// Trust & Safety admin panel — word blocklist + viewer's own blocked-users list.
// Only rendered inside the admin dashboard section.
const TrustSafetyPanel = ({ token, isAdmin }) => {
  const [words, setWords] = useState([]);
  const [wordsLoading, setWordsLoading] = useState(true);
  const [newWord, setNewWord] = useState("");
  const [addingWord, setAddingWord] = useState(false);
  const [myBlocks, setMyBlocks] = useState([]);
  const [blocksLoading, setBlocksLoading] = useState(true);
  const [verifQueue, setVerifQueue] = useState({ total: 0, pending: 0 });

  const loadVerifQueue = async () => {
    if (!isAdmin) return;
    try {
      const res = await axios.get(`${API}/admin/verifications/queue`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const rows = res.data?.rows || [];
      const pending = rows.filter((r) => r.status !== "approved" && r.status !== "denied").length;
      setVerifQueue({ total: rows.length, pending });
    } catch (_e) { /* silent — queue widget is best-effort */ }
  };

  const loadWords = async () => {
    if (!isAdmin) return;
    setWordsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/blocklist/words`, { headers: { Authorization: `Bearer ${token}` } });
      setWords(res.data.words || []);
    } catch (e) { /* ignore */ } finally { setWordsLoading(false); }
  };

  const loadMyBlocks = async () => {
    setBlocksLoading(true);
    try {
      const res = await axios.get(`${API}/users/me/blocked`, { headers: { Authorization: `Bearer ${token}` } });
      setMyBlocks(res.data || []);
    } catch (e) { /* ignore */ } finally { setBlocksLoading(false); }
  };

  useEffect(() => { loadWords(); loadMyBlocks(); loadVerifQueue(); }, [token]);

  const addWord = async () => {
    const w = newWord.trim();
    if (!w) return;
    setAddingWord(true);
    try {
      await axios.post(`${API}/admin/blocklist/words`, { word: w }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Blocked: "${w}"`);
      setNewWord("");
      loadWords();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add");
    } finally { setAddingWord(false); }
  };

  const removeWord = async (w) => {
    try {
      await axios.delete(`${API}/admin/blocklist/words/${encodeURIComponent(w)}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Removed: "${w}"`);
      loadWords();
    } catch (e) { toast.error("Could not remove"); }
  };

  const unblock = async (uid) => {
    try {
      await axios.delete(`${API}/users/${uid}/block`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Unblocked");
      loadMyBlocks();
    } catch (e) { toast.error("Could not unblock"); }
  };

  return (
    <div className="space-y-8" data-testid="trust-safety-panel">
      <div>
        <div className="flex items-center gap-3 mb-4">
          <Shield className="w-6 h-6 text-[#EC008C]" />
          <div>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white">Trust &amp; Safety</h2>
            <p className="text-sm text-[#8A92A0]">Keep the community respectful — manage the word blocklist and the users you&apos;ve muted.</p>
          </div>
        </div>
      </div>

      {isAdmin && (
        <Link
          to="/admin/verifications"
          className="card p-5 flex items-center gap-4 hover:border-[#EC008C]/50 transition-all group"
          data-testid="trust-verification-card"
        >
          <div className="w-12 h-12 rounded-xl bg-[#EC008C]/15 border border-[#EC008C]/30 flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-6 h-6 text-[#EC008C]" />
          </div>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold">Artist KYC Queue</p>
            <h3 className="text-white font-bold text-lg">
              Verification Portal
              {verifQueue.pending > 0 && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-[#F0C464] text-black text-[10px] font-bold" data-testid="trust-verif-pending-badge">
                  {verifQueue.pending} pending
                </span>
              )}
            </h3>
            <p className="text-xs text-[#8A92A0]">Review ID uploads, AI deepfake scans, and approve artists for payouts.</p>
          </div>
          <ArrowRight className="w-5 h-5 text-[#8A92A0] group-hover:text-[#EC008C] group-hover:translate-x-1 transition-all" />
        </Link>
      )}

      {isAdmin && (
        <section className="card p-6" data-testid="blocklist-words-section">
          <h3 className="font-['Unbounded'] text-base font-bold text-white mb-1">Word Blocklist</h3>
          <p className="text-xs text-[#8A92A0] mb-4">
            Any post containing these words/phrases (case-insensitive) is instantly rejected at post time. Whole-word match for single words; substring match for multi-word phrases.
          </p>

          <div className="flex gap-2 mb-5">
            <input
              type="text"
              value={newWord}
              onChange={(e) => setNewWord(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addWord()}
              placeholder="Add a word or short phrase..."
              maxLength={100}
              className="flex-1 bg-[#0A0E18] border border-[#1A2030] rounded-lg px-3 py-2 text-sm text-white placeholder-[#6A7280] focus:outline-none focus:border-[#EC008C]/50"
              data-testid="new-blocklist-word-input"
            />
            <Button
              onClick={addWord}
              disabled={addingWord || !newWord.trim()}
              className="bg-[#EC008C] text-black hover:bg-[#FF3399] rounded-lg font-semibold flex items-center gap-1"
              data-testid="add-blocklist-word-btn"
            >
              {addingWord ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add
            </Button>
          </div>

          {wordsLoading ? (
            <div className="text-sm text-[#8A92A0]">Loading…</div>
          ) : words.length === 0 ? (
            <div className="text-sm text-[#8A92A0]">No blocked words configured.</div>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="blocklist-words-chips">
              {words.map((w) => (
                <div key={w} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0A0E18] border border-[#1A2030] text-sm text-white" data-testid={`blocklist-word-${w}`}>
                  <span>{w}</span>
                  <button
                    onClick={() => removeWord(w)}
                    className="text-[#8A92A0] hover:text-[#D14A85]"
                    title={`Remove "${w}"`}
                    data-testid={`remove-blocklist-word-${w}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <section className="card p-6" data-testid="my-blocks-section">
        <h3 className="font-['Unbounded'] text-base font-bold text-white mb-1 flex items-center gap-2">
          <UserX className="w-4 h-4 text-[#EC008C]" />
          People I&apos;ve Blocked
        </h3>
        <p className="text-xs text-[#8A92A0] mb-4">
          Blocked users won&apos;t appear in your Community/Trending feeds. Only you see this list.
        </p>

        {blocksLoading ? (
          <div className="text-sm text-[#8A92A0]">Loading…</div>
        ) : myBlocks.length === 0 ? (
          <div className="text-sm text-[#8A92A0]">You haven&apos;t blocked anyone.</div>
        ) : (
          <ul className="space-y-2" data-testid="my-blocks-list">
            {myBlocks.map((b) => (
              <li key={b.blocked_id} className="flex items-center justify-between p-3 rounded-lg border border-[#1A2030] bg-[#0A0E18]" data-testid={`my-block-${b.blocked_id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-[#1A2030] flex items-center justify-center overflow-hidden flex-shrink-0">
                    {b.blocked_picture ? <img src={b.blocked_picture} alt="" className="w-full h-full object-cover" /> : <UserX className="w-4 h-4 text-[#8A92A0]" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-white truncate">{b.blocked_name}</p>
                    <p className="text-xs text-[#4A5260] truncate">Blocked {new Date(b.created_at).toLocaleDateString()}</p>
                  </div>
                </div>
                <Button
                  onClick={() => unblock(b.blocked_id)}
                  variant="outline"
                  className="border-[#1A2030] text-[#8A92A0] hover:text-white h-8 text-xs px-3"
                  data-testid={`unblock-btn-${b.blocked_id}`}
                >
                  Unblock
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default TrustSafetyPanel;
